import {
  F01_MAIN_HELP_LEADS,
  type CoachHandoffAction,
  type CoachSummary,
} from "@care-by-ella/contracts";
import {
  COACH_BEFORE_SUBSCRIPTION_LINE,
  COACH_INACTIVE_LINE,
  fmtDay,
} from "./coach-now";

// TES-1101 — the coach's top summary as sentences.
//
// Pure, so the wording is testable without a DOM. Every sentence says where
// it comes from and when ("Källa · datum"), and a missing fact is said to be
// missing rather than left out (A-01: "När underlag saknas ska
// sammanfattningen visa att uppgiften ännu saknas").

export interface SummaryStep {
  text: string;
  /** Source and date, as the coach reads it. */
  detail: string | null;
  /** Where on the page the coach acts on it. */
  href: string | null;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many.replace("{n}", String(n));
}

const dated = (source: string, at: string | null) =>
  at ? `${source} · ${fmtDay(at)}` : source;

export function nextStepOf(s: CoachSummary): SummaryStep {
  const n = s.nextStep;
  switch (n.kind) {
    case "no_coach_access":
      return { text: COACH_INACTIVE_LINE, detail: "Abonnemanget", href: null };
    case "no_coach_before_subscription":
      return {
        text: COACH_BEFORE_SUBSCRIPTION_LINE,
        detail: "Abonnemanget",
        href: null,
      };
    case "message":
      return {
        text: plural(
          n.count,
          "Svara på ett meddelande i chatten",
          "Svara på {n} meddelanden i chatten",
        ),
        detail: dated("Coachchatten, senaste", n.at),
        href: "#chat",
      };
    case "checkin":
      return {
        text: plural(
          n.count,
          "Svara på en veckoincheckning",
          "Svara på {n} veckoincheckningar",
        ),
        detail: dated("Veckoincheckning (F-04)", n.at),
        // TES-1206 — the check-ins have their own tab.
        href: "#incheckningar",
      };
    case "follow_up_due":
      return {
        text: n.prompt ?? "En uppföljning ska göras nu",
        detail: dated("Din uppföljning, skulle göras", n.at),
        href: "#aktuellt",
      };
    case "handoff": {
      const action = s.handoffs.toCoach.action;
      return {
        text: action
          ? `${HANDOFF_ACTION_TEXT[action]}${n.count > 1 ? ` (${n.count} överlämningar)` : ""}`
          : plural(
              n.count,
              "Hantera överlämningen till dig",
              "Hantera {n} överlämningar till dig",
            ),
        detail: dated("Överlämning", n.at),
        href: "#handoff",
      };
    }
    case "follow_up_scheduled":
      return {
        text: n.prompt ?? "Planerad uppföljning",
        detail: dated("Din uppföljning, planerad", n.at),
        href: "#aktuellt",
      };
    case "checkin_window_open":
      return {
        text: "Inget väntar på dig just nu – veckans incheckning är öppen",
        detail: dated("Veckoincheckning (F-04), öppen sedan", n.at),
        href: null,
      };
    case "next_checkin":
      return {
        text: "Inget väntar på dig just nu",
        detail: dated("Nästa veckoincheckning öppnar", n.at),
        href: null,
      };
    case "no_checkin_without_subscription":
      return {
        text: "Inget väntar på dig just nu",
        detail: "Ingen veckoincheckning – abonnemanget är inte aktivt",
        href: null,
      };
    default:
      // An admin tab opened before a deploy can receive a step kind it was
      // built without. Fall back to a neutral line instead of returning
      // undefined, which took down the whole patient page (prod, 2026-10-06).
      return { text: "Inget väntar på dig just nu", detail: null, href: null };
  }
}

// F-01's lead-in ("Viktigast just nu: …") is our label, and the block's
// heading already says it; her own words after it are shown as stored.
export function mainHelpText(body: string): string {
  for (const lead of F01_MAIN_HELP_LEADS) {
    if (body.startsWith(`${lead}: `)) return body.slice(lead.length + 2);
  }
  return body;
}

export function alsoWaitingLine(s: CoachSummary): string | null {
  const n = s.nextStep.alsoWaiting;
  return n === 0 ? null : plural(n, "+ 1 till väntar", "+ {n} till väntar");
}

// TES-1402 (Ö-550) — the step the coach takes next, named after the button
// she presses in the handoff block (PatientOpenHandoffs: "Bekräfta", "Påbörja",
// "Markera som löst"). Only the step: the block never shows the reason.
export const HANDOFF_ACTION_TEXT: Record<CoachHandoffAction, string> = {
  acknowledge: "Bekräfta överlämningen",
  start: "Påbörja överlämningen",
  resolve: "Slutför och markera överlämningen som löst",
};

export function handoffLines(s: CoachSummary): string[] {
  const { toCoach, fromCoach } = s.handoffs;
  const lines: string[] = [];
  if (toCoach.open > 0) {
    lines.push(
      `${plural(toCoach.open, "1 öppen till dig", "{n} öppna till dig")}${toCoach.latestAt ? `, senast ${fmtDay(toCoach.latestAt)}` : ""}`,
    );
    if (toCoach.action) {
      lines.push(`Att göra: ${HANDOFF_ACTION_TEXT[toCoach.action]}`);
    }
  }
  if (fromCoach.open > 0) {
    lines.push(
      `${plural(fromCoach.open, "1 från dig väntar på läkaren", "{n} från dig väntar på läkaren")}${fromCoach.latestAt ? `, senast ${fmtDay(fromCoach.latestAt)}` : ""}`,
    );
  }
  return lines.length > 0 ? lines : ["Inga öppna överlämningar"];
}
